(function () {
  'use strict';

  /* ═══════════════════════════════════════════════════════════════════
     Just Dropped — JS
     stepone.life · Shopify OS 2.0

     Responsibilities:
       1. Swatch colours — fetch from /collections/all?view=swatches
          and apply to .jd-swatch-btn elements by handle.
       2. Swatch click — swap card image, highlight ring, update
          the Quick Add button's target product.
       3. Quick Add — open a size-selector drawer (never direct-to-cart).
       4. Size drawer — fetch /products/{handle}.js, render size pills,
          add chosen size to cart, fire theme cart events.
       5. V2 auto-rotate + IntersectionObserver pager.
     ═══════════════════════════════════════════════════════════════════ */

  /* ── 0. GA4 event helper ─────────────────────────────────────────── */

  function pushGaEvent(eventName, params) {
    if (typeof gtag !== 'function') return;
    gtag('event', eventName, params);
  }

  /* ── 0b. Money formatter ─────────────────────────────────────────── */

  function formatMoney(cents) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
    try {
      return (cents / 100).toLocaleString('en', { style: 'currency', currency: currency });
    } catch (e) {
      return '£' + (cents / 100).toFixed(2);
    }
  }

  /* ── 1. Swatch colour data ────────────────────────────────────────── */

  var swatchMapPromise = null;

  function loadSwatchMap() {
    if (swatchMapPromise) return swatchMapPromise;
    swatchMapPromise = fetch(Shopify.routes.root + 'collections/all?view=swatches')
      .then(function (r) {
        if (!r.ok) throw new Error('swatch endpoint error');
        return r.json();
      })
      .then(function (data) {
        var map = {};
        var products = (data && data.products) ? data.products : [];
        products.forEach(function (p) {
          if (p.handle && p.swatch) map[p.handle] = p.swatch;
        });
        return map;
      })
      .catch(function () { return {}; });
    return swatchMapPromise;
  }

  function applySwatchColor(btn, swatchValue) {
    if (!swatchValue) return;
    /* hex / rgb / hsl → background-color; anything else → background-image */
    if (/^#[0-9a-f]{3,8}$/i.test(swatchValue) || /^(rgb|hsl)/i.test(swatchValue)) {
      btn.style.backgroundColor = swatchValue;
    } else {
      btn.style.backgroundImage = 'url(' + swatchValue + ')';
      btn.style.backgroundSize  = 'cover';
    }
  }

  /* ── 2. Swatch interactivity ─────────────────────────────────────── */

  function initSwatches(section) {
    var swatchBtns = Array.from(section.querySelectorAll('.jd-swatch-btn'));
    if (!swatchBtns.length) return;

    /* Apply hex overrides immediately (no API needed) */
    swatchBtns.forEach(function (btn) {
      if (btn.dataset.swatchOverride) {
        applySwatchColor(btn, btn.dataset.swatchOverride);
      }
    });

    /* Fetch API colours and fill in the rest */
    loadSwatchMap().then(function (map) {
      swatchBtns.forEach(function (btn) {
        if (btn.dataset.swatchOverride) return; /* override wins */
        var handle = btn.dataset.swatchHandle;
        if (handle && map[handle]) {
          applySwatchColor(btn, map[handle]);
        }
      });
      /* Run auto-swatches after colour map is applied so manual overrides are set first */
      section.querySelectorAll('.jd-card').forEach(function (card) {
        var h = card.dataset.productHandle;
        if (h) autoSwatches(card, h);
      });
    });

    /* Click — swap image, highlight ring, update Quick Add target */
    swatchBtns.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        var card = btn.closest('.jd-card');
        if (!card) return;

        /* Swap product image */
        var img    = card.querySelector('.jd-card__main-img');
        var newSrc = btn.dataset.imgSrc;
        if (img && newSrc) {
          img.src    = newSrc;
          img.srcset = '';          /* clear so browser uses the switched src */
        }

        /* Update product title */
        var titleEl = card.querySelector('.jd-card__name, .jd-v2-card__name');
        if (titleEl && btn.dataset.productTitle) {
          titleEl.textContent = btn.dataset.productTitle;
        }

        /* Update product type line (e.g. "Underwear" → "Sports Bra") */
        var typeEl = card.querySelector('.jd-card__line, .jd-v2-card__line');
        if (typeEl && btn.dataset.productType) {
          typeEl.textContent = btn.dataset.productType;
        }

        /* Update card link href and aria-label */
        if (btn.dataset.productUrl) {
          card.href = btn.dataset.productUrl;
        }
        if (btn.dataset.productTitle) {
          card.setAttribute('aria-label', btn.dataset.productTitle);
        }

        /* Redirect Quick Add to the clicked swatch's product */
        var qaBtn = card.querySelector('[data-jd-quick-add]');
        if (qaBtn) {
          qaBtn.dataset.productHandle = btn.dataset.handle;
          qaBtn.dataset.productTitle  = btn.dataset.productTitle || '';
        }

        /* Selection ring — one active at a time per card */
        card.querySelectorAll('.jd-swatch-btn').forEach(function (s) {
          s.classList.remove('is-selected');
        });
        btn.classList.add('is-selected');
      });
    });
  }

  /* ── 2b. Auto-swatches from product colour variants ─────────────── */

  /* If a card has no manual sub-product swatches, read the embedded variant
     data and auto-generate a swatch button per colour option value.
     Each button uses the first variant image for that colour as a thumbnail.
     Clicking a swatch pre-selects that colour in the size drawer. */
  function autoSwatches(card, handle) {
    var swatchContainer = card.querySelector('.jd-swatches');
    if (!swatchContainer) return;

    /* Only activate when no manual sub-products have been added (1 = main swatch only) */
    if (swatchContainer.querySelectorAll('.jd-swatch-btn').length !== 1) return;

    var product  = getProductData(handle);
    if (!product) return;

    var options  = product.options  || [];
    var variants = product.variants || [];

    /* Find the Colour option */
    var colourIdx = -1;
    for (var i = 0; i < options.length; i++) {
      var n = (typeof options[i] === 'string' ? options[i] : (options[i].name || '')).toLowerCase();
      if (n === 'colour' || n === 'color' || n === 'couleur' || n === 'color / colour') {
        colourIdx = i;
        break;
      }
    }
    if (colourIdx === -1) return;

    /* Collect unique colour values; record the first variant image for each */
    var seen = {}, colours = [];
    variants.forEach(function (v) {
      var val = (v.options && v.options[colourIdx]) || v['option' + (colourIdx + 1)] || '';
      if (val && !seen[val]) {
        seen[val] = true;
        colours.push({ name: val, img: v.featured_image });
      }
    });
    if (colours.length <= 1) return; /* single colour — nothing to generate */

    /* Replace the lone main-product swatch with per-colour buttons */
    swatchContainer.innerHTML = '';
    swatchContainer.dataset.count = colours.length; /* drives the CSS review-count hide rule */

    colours.forEach(function (colour, i) {
      var btn       = document.createElement('button');
      btn.type      = 'button';
      btn.className = 'jd-swatch-btn' + (i === 0 ? ' is-selected' : '');
      btn.setAttribute('aria-label', colour.name);
      btn.dataset.colourVal = colour.name;

      /* Use the variant image as a round thumbnail */
      if (colour.img && colour.img.src) {
        btn.style.backgroundImage    = 'url(' + colour.img.src + ')';
        btn.style.backgroundSize     = 'cover';
        btn.style.backgroundPosition = 'center';
      }

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        /* Swap card hero image */
        var imgEl = card.querySelector('.jd-card__main-img');
        if (imgEl && colour.img && colour.img.src) {
          imgEl.src    = colour.img.src;
          imgEl.srcset = '';
        }

        /* Selection ring */
        swatchContainer.querySelectorAll('.jd-swatch-btn').forEach(function (s) {
          s.classList.remove('is-selected');
        });
        btn.classList.add('is-selected');

        /* Tell the Quick Add drawer which colour to pre-select */
        var qaBtn = card.querySelector('[data-jd-quick-add]');
        if (qaBtn) qaBtn.dataset.selectedColour = colour.name;
      });

      swatchContainer.appendChild(btn);
    });
  }

  /* ── 3. Size-selector drawer ─────────────────────────────────────── */

  /* Read variant/option data embedded by Liquid in <script type="application/json"> */
  function getProductData(handle) {
    var el = document.querySelector('script.jd-product-data[data-handle="' + handle + '"]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /* Render option groups from a product data object (Liquid or AJAX format).
     All product options (Colour, Size, etc.) render as selector pills.
     A hardcoded Pack Size group (4 / 7 / 15) always appears at the bottom —
     clicking a pack button resolves the current selection and adds that qty to cart. */
  function renderSizes(product, sizesEl, feedbackEl, productTitle, preselectedColour, priceEl) {
    sizesEl.innerHTML = '';
    var options  = product.options  || [];
    var variants = product.variants || [];

    if (!variants.length) {
      sizesEl.innerHTML = '<div class="jd-drawer__error">No sizes available.</div>';
      return;
    }

    /* Get a variant's value for a specific option index (handles Liquid + AJAX formats) */
    function getVal(v, idx) {
      return (v.options && v.options[idx]) || v['option' + (idx + 1)] || '';
    }

    /* Pre-select the first available value for every option.
       If a colour was pre-selected via an auto-swatch click, honour it. */
    var sel = {};
    options.forEach(function (opt, idx) {
      var optName = (typeof opt === 'string' ? opt : (opt.name || '')).toLowerCase();
      var isColour = optName === 'colour' || optName === 'color' || optName === 'couleur';
      if (isColour && preselectedColour) {
        sel[idx] = preselectedColour;
        return;
      }
      for (var i = 0; i < variants.length; i++) {
        var val = getVal(variants[i], idx);
        if (val) { sel[idx] = val; break; }
      }
    });

    /* Resolve the variant that matches all current selections */
    function findVariant() {
      return variants.find(function (v) {
        return options.every(function (opt, idx) {
          var chosen = sel[idx];
          if (!chosen) return true;
          return getVal(v, idx) === chosen;
        });
      }) || null;
    }

    /* Update the price element to reflect the current variant selection */
    function updatePrice() {
      if (!priceEl) return;
      var v = findVariant();
      priceEl.textContent = v ? formatMoney(v.price) : '';
    }

    /* Enable / disable pack buttons based on whether the current selection is available */
    function refreshPackGroup() {
      var pg = sizesEl.querySelector('.jd-drawer__group--pack');
      if (!pg) return;
      var variant = findVariant();
      var ok = !!(variant && variant.available);
      pg.querySelectorAll('.jd-pack-btn').forEach(function (b) {
        b.disabled = !ok;
        b.classList.toggle('jd-size-btn--sold-out', !ok);
      });
      updatePrice();
    }

    /* Add the resolved variant at the given quantity to cart */
    function doAddToCart(variant, quantity) {
      var allBtns = Array.from(sizesEl.querySelectorAll('.jd-size-btn, .jd-pack-btn'));
      allBtns.forEach(function (b) { b.disabled = true; });
      feedbackEl.textContent = 'Adding…';

      /* Mirror the theme's product-form pattern: collect section IDs first so
         cart:change uses createContextualFragment and re-executes inline scripts
         (critical for s1-tiered-discount-b when cart starts empty). */
      var sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));

      fetch(Shopify.routes.root + 'cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body:    JSON.stringify({ id: variant.id, quantity: quantity, sections: sectionsToBundle }),
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (d) { throw d; });
          return r.json();
        })
        .then(function (addResponse) {
          feedbackEl.textContent = '✓ Added to cart';
          pushGaEvent('add_to_cart', {
            items: [{ item_id: String(variant.id), item_name: productTitle || '', quantity: quantity }]
          });
          /* Fetch full cart, merge rendered sections, then fire cart:change.
             cart:change → _onCartChanged → createContextualFragment → scripts execute. */
          return fetch(Shopify.routes.root + 'cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cartContent) {
              cartContent['sections'] = addResponse['sections'];
              document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
                bubbles: true,
                detail: { baseEvent: 'variant:add', cart: cartContent }
              }));
              document.dispatchEvent(new CustomEvent('variant:add', { detail: { variantId: variant.id } }));
              document.dispatchEvent(new CustomEvent('cart:updated'));
              document.dispatchEvent(new CustomEvent('cart:add', { detail: { variantId: variant.id } }));
              setTimeout(function () {
                if (drawer) {
                  drawer.classList.remove('is-open');
                  drawer.setAttribute('aria-hidden', 'true');
                }
                allBtns.forEach(function (b) { b.disabled = false; });
              }, 900);
            });
        })
        .catch(function (err) {
          feedbackEl.textContent = (err && err.description) ? err.description : 'Could not add to cart.';
          allBtns.forEach(function (b) {
            if (!b.classList.contains('jd-size-btn--sold-out')) b.disabled = false;
          });
        });
    }

    /* ── Render each product option as a labelled selector group ── */
    options.forEach(function (opt, optIdx) {
      var name = typeof opt === 'string' ? opt : (opt.name || '');

      var seen = {}, values = [];
      variants.forEach(function (v) {
        var val = getVal(v, optIdx);
        if (val && !seen[val]) { seen[val] = true; values.push(val); }
      });
      if (!values.length) return;

      var group = document.createElement('div');
      group.className = 'jd-drawer__group';

      var lbl = document.createElement('p');
      lbl.className = 'jd-drawer__group-label';
      lbl.textContent = name;
      group.appendChild(lbl);

      var pills = document.createElement('div');
      pills.className = 'jd-drawer__pills';
      group.appendChild(pills);

      values.forEach(function (val) {
        var btn = document.createElement('button');
        btn.type        = 'button';
        btn.textContent = val;
        btn.dataset.val = val;
        btn.className   = 'jd-size-btn jd-selector-btn' + (sel[optIdx] === val ? ' is-selected' : '');

        btn.addEventListener('click', function () {
          sel[optIdx] = val;
          pills.querySelectorAll('.jd-selector-btn').forEach(function (b) {
            b.classList.toggle('is-selected', b.dataset.val === val);
          });
          refreshPackGroup(); /* also calls updatePrice() */
        });

        pills.appendChild(btn);
      });

      sizesEl.appendChild(group);
    });

    /* ── No options at all — single "Add to cart" fallback (no pack group) ── */
    if (!options.length) {
      var v0 = variants[0];
      if (v0) {
        var grp = document.createElement('div');
        grp.className = 'jd-drawer__group';
        var pl = document.createElement('div');
        pl.className = 'jd-drawer__pills';
        pl.appendChild(makeSizeBtn('Add to cart', v0, feedbackEl, productTitle));
        grp.appendChild(pl);
        sizesEl.appendChild(grp);
      } else {
        sizesEl.innerHTML = '<div class="jd-drawer__error">No sizes available.</div>';
      }
      return;
    }

    /* ── Always render Pack Size group at the bottom ── */
    var PACK_SIZES = [
      { label: 'Single',  qty: 1  },
      { label: '4 Pack',  qty: 4  },
      { label: '7 Pack',  qty: 7  },
      { label: '15 Pack', qty: 15 },
    ];

    var packGroup = document.createElement('div');
    packGroup.className = 'jd-drawer__group jd-drawer__group--pack';

    var packLbl = document.createElement('p');
    packLbl.className = 'jd-drawer__group-label';
    packLbl.textContent = 'Pack Size';
    packGroup.appendChild(packLbl);

    var packPromo = document.createElement('p');
    packPromo.className = 'jd-drawer__group-promo';
    packPromo.textContent = 'Save up to 20% off when you buy in bulk';
    packGroup.appendChild(packPromo);

    var packPills = document.createElement('div');
    packPills.className = 'jd-drawer__pills';
    packGroup.appendChild(packPills);

    var initialVariant = findVariant();
    var initialOk = !!(initialVariant && initialVariant.available);

    PACK_SIZES.forEach(function (pack) {
      var btn = document.createElement('button');
      btn.type        = 'button';
      btn.textContent = pack.label;
      btn.dataset.qty = pack.qty;
      btn.className   = 'jd-size-btn jd-pack-btn';
      btn.disabled    = !initialOk;
      if (!initialOk) btn.classList.add('jd-size-btn--sold-out');

      btn.addEventListener('click', function () {
        var variant = findVariant();
        if (!variant || !variant.available) return;
        /* Show selected state while cart request processes */
        packPills.querySelectorAll('.jd-pack-btn').forEach(function (b) {
          b.classList.toggle('is-selected', b === btn);
        });
        doAddToCart(variant, pack.qty);
      });

      packPills.appendChild(btn);
    });

    sizesEl.appendChild(packGroup);

    /* Show price for the initially selected variant */
    updatePrice();
  }

  var drawer = null;

  function buildDrawer() {
    var el = document.createElement('div');
    el.id        = 'jd-size-drawer';
    el.className = 'jd-drawer';
    el.setAttribute('role',       'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-labelledby', 'jd-drawer-title');
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = [
      '<div class="jd-drawer__overlay"></div>',
      '<div class="jd-drawer__panel">',
      '  <div class="jd-drawer__head">',
      '    <span class="jd-drawer__title" id="jd-drawer-title">Select a size</span>',
      '    <button class="jd-drawer__close" type="button" aria-label="Close">',
      '      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">',
      '        <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
      '      </svg>',
      '    </button>',
      '  </div>',
      '  <div class="jd-drawer__product-name"></div>',
      '  <div class="jd-drawer__price"></div>',
      '  <div class="jd-drawer__sizes"></div>',
      '  <div class="jd-drawer__feedback"></div>',
      '</div>'
    ].join('');
    document.body.appendChild(el);

    el.querySelector('.jd-drawer__overlay').addEventListener('click', closeDrawer);
    el.querySelector('.jd-drawer__close').addEventListener('click', closeDrawer);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeDrawer();
    });

    return el;
  }

  function openDrawer(productHandle, productTitle, preselectedColour) {
    drawer = drawer || buildDrawer();

    var nameEl     = drawer.querySelector('.jd-drawer__product-name');
    var priceEl    = drawer.querySelector('.jd-drawer__price');
    var sizesEl    = drawer.querySelector('.jd-drawer__sizes');
    var feedbackEl = drawer.querySelector('.jd-drawer__feedback');

    nameEl.textContent     = productTitle || '';
    priceEl.textContent    = '';
    sizesEl.innerHTML      = '<div class="jd-drawer__loading">Loading sizes…</div>';
    feedbackEl.textContent = '';

    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    drawer.querySelector('.jd-drawer__close').focus();

    /* ── Try embedded Liquid data first (instant, no network) ── */
    var embeddedData = getProductData(productHandle);
    if (embeddedData) {
      renderSizes(embeddedData, sizesEl, feedbackEl, productTitle, preselectedColour || '', priceEl);
      return;
    }

    /* ── Fall back to AJAX if embedded data isn't present ── */
    fetch(Shopify.routes.root + 'products/' + productHandle + '.js')
      .then(function (r) {
        if (!r.ok) throw new Error('product fetch failed');
        return r.json();
      })
      .then(function (product) {
        renderSizes(product, sizesEl, feedbackEl, productTitle, preselectedColour || '', priceEl);
      })
      .catch(function (err) {
        console.error('[Just Dropped] size fetch failed for "' + productHandle + '":', err);
        sizesEl.innerHTML = '<div class="jd-drawer__error">Could not load sizes. Please visit the product page.</div>';
      });
  }

  function makeSizeBtn(label, variant, feedbackEl, productTitle) {
    var btn    = document.createElement('button');
    btn.type   = 'button';
    btn.className = 'jd-size-btn' + (variant.available ? '' : ' jd-size-btn--sold-out');
    btn.textContent = label;
    if (!variant.available) {
      btn.disabled = true;
      return btn;
    }

    btn.addEventListener('click', function () {
      var panel   = feedbackEl.closest('.jd-drawer__panel');
      var allBtns = Array.from(panel.querySelectorAll('.jd-size-btn'));

      allBtns.forEach(function (b) { b.disabled = true; });
      feedbackEl.textContent = 'Adding…';

      var sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));

      fetch(Shopify.routes.root + 'cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body:    JSON.stringify({ id: variant.id, quantity: 1, sections: sectionsToBundle }),
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (d) { throw d; });
          return r.json();
        })
        .then(function (addResponse) {
          feedbackEl.textContent = '✓ Added to cart';
          pushGaEvent('add_to_cart', {
            items: [{ item_id: String(variant.id), item_name: productTitle || '' }]
          });
          return fetch(Shopify.routes.root + 'cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cartContent) {
              cartContent['sections'] = addResponse['sections'];
              document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
                bubbles: true,
                detail: { baseEvent: 'variant:add', cart: cartContent }
              }));
              document.dispatchEvent(new CustomEvent('variant:add', { detail: { variantId: variant.id } }));
              document.dispatchEvent(new CustomEvent('cart:updated'));
              document.dispatchEvent(new CustomEvent('cart:add', { detail: { variantId: variant.id } }));
              setTimeout(function () {
                if (drawer) {
                  drawer.classList.remove('is-open');
                  drawer.setAttribute('aria-hidden', 'true');
                }
                allBtns.forEach(function (b) {
                  if (!b.classList.contains('jd-size-btn--sold-out')) b.disabled = false;
                });
              }, 900);
            });
        })
        .catch(function (err) {
          feedbackEl.textContent = (err && err.description) ? err.description : 'Could not add to cart.';
          allBtns.forEach(function (b) {
            if (!b.classList.contains('jd-size-btn--sold-out')) b.disabled = false;
          });
        });
    });

    return btn;
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    /* Only restore scroll if the side cart is not also open */
    var sideCartOpen = !!document.querySelector('cart-drawer[open]');
    if (!sideCartOpen) document.body.style.overflow = '';
  }

  /* ── 4. Quick Add button ─────────────────────────────────────────── */

  function initQuickAdd(section) {
    section.querySelectorAll('[data-jd-quick-add]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (btn.disabled) return;
        pushGaEvent('quick_add_open', {
          item_id:   btn.dataset.productHandle || '',
          item_name: btn.dataset.productTitle  || ''
        });
        openDrawer(btn.dataset.productHandle, btn.dataset.productTitle, btn.dataset.selectedColour || '');
      });
    });
  }

  /* ── Analytics: card click to PDP ───────────────────────────────── */

  function initCardTracking(section) {
    section.querySelectorAll('.jd-card').forEach(function (card) {
      card.addEventListener('click', function (e) {
        /* Ignore clicks on swatches and quick-add — those have their own events */
        if (e.target.closest('.jd-swatch-btn, [data-jd-quick-add]')) return;
        pushGaEvent('select_item', {
          item_list_name: 'Just Dropped',
          items: [{
            item_id:   card.dataset.productHandle || '',
            item_name: card.getAttribute('aria-label') || ''
          }]
        });
      });
    });
  }

  /* ── 5. Desktop nav arrows ──────────────────────────────────────── */

  function initNavArrows(section) {
    var track   = section.querySelector('.jd-track');
    var prevBtn = section.querySelector('.jd-nav-btn--prev');
    var nextBtn = section.querySelector('.jd-nav-btn--next');

    if (!track || !prevBtn || !nextBtn) return;

    function getScrollAmount() {
      var style = getComputedStyle(section);
      var cardW = parseFloat(style.getPropertyValue('--jd-card-w')) || 240;
      var gap   = parseFloat(style.getPropertyValue('--jd-gap'))    || 20;
      return cardW + gap;
    }

    function updateArrows() {
      var atStart = track.scrollLeft <= 2;
      var atEnd   = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
      prevBtn.classList.toggle('is-hidden', atStart);
      nextBtn.classList.toggle('is-hidden', atEnd);
      prevBtn.disabled = atStart;
      nextBtn.disabled = atEnd;
    }

    prevBtn.addEventListener('click', function () {
      track.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' });
    });

    nextBtn.addEventListener('click', function () {
      track.scrollBy({ left: getScrollAmount(), behavior: 'smooth' });
    });

    track.addEventListener('scroll', updateArrows, { passive: true });

    /* Set initial state — prev hidden at page load (scrolled to start) */
    updateArrows();
  }

  /* ── 6. V1 bottom dots ──────────────────────────────────────────── */

  function initV1Dots(section) {
    var track      = section.querySelector('.jd-track');
    var items      = Array.from(section.querySelectorAll('.jd-item'));
    var bottomDots = Array.from(section.querySelectorAll('.jd-bottom-dot'));

    if (!track || !items.length || !bottomDots.length) return;

    function setDotActive(idx) {
      bottomDots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === idx);
        d.setAttribute('aria-current', i === idx ? 'true' : 'false');
      });
    }

    var observer = new IntersectionObserver(function (entries) {
      var best = null;
      entries.forEach(function (e) {
        if (!best || e.intersectionRatio > best.intersectionRatio) best = e;
      });
      if (best && best.intersectionRatio >= 0.4) {
        var idx = items.indexOf(best.target);
        if (idx !== -1) setDotActive(idx);
      }
    }, { root: track, threshold: [0, 0.4, 0.6, 1] });

    items.forEach(function (item) { observer.observe(item); });

    bottomDots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        var item = items[i];
        if (!item) return;
        var pad = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 20;
        track.scrollTo({ left: Math.max(0, item.offsetLeft - pad), behavior: 'smooth' });
      });
    });
  }

  /* ── 6. V2 auto-rotate + pager ───────────────────────────────────── */

  function initV2(section) {
    var track      = section.querySelector('.jd-track');
    var items      = Array.from(section.querySelectorAll('.jd-item'));
    var dots       = Array.from(section.querySelectorAll('.jd-dot'));
    var bottomDots = Array.from(section.querySelectorAll('.jd-bottom-dot'));
    var counterEl  = section.querySelector('.jd-counter__active');
    var pauseBtn   = section.querySelector('.jd-pause-btn');

    if (!track || !items.length) return;

    var autorotateMs    = parseInt(section.dataset.autorotate, 10) || 4500;
    var reducedMotion   = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var activeIndex     = 0;
    var paused          = false;
    var manuallyPaused  = false;
    var idleTimer       = null;
    var rotateTimer     = null;

    function setActive(idx) {
      activeIndex = idx;
      if (counterEl) counterEl.textContent = String(idx + 1).padStart(2, '0');
      dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === idx);
        d.setAttribute('aria-current', i === idx ? 'true' : 'false');
      });
      bottomDots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === idx);
        d.setAttribute('aria-current', i === idx ? 'true' : 'false');
      });
      items.forEach(function (item, i) {
        var inner = item.querySelector('.jd-v2-card__inner');
        if (inner) inner.classList.toggle('is-active', i === idx);
      });
    }

    function scrollToItem(idx) {
      var item = items[idx];
      if (!item) return;
      var pad = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 20;
      track.scrollTo({ left: Math.max(0, item.offsetLeft - pad), behavior: 'smooth' });
    }

    var observer = new IntersectionObserver(function (entries) {
      var best = null;
      entries.forEach(function (e) {
        if (!best || e.intersectionRatio > best.intersectionRatio) best = e;
      });
      if (best && best.intersectionRatio >= 0.4) {
        var idx = items.indexOf(best.target);
        if (idx !== -1) setActive(idx);
      }
    }, { root: track, threshold: [0, 0.4, 0.6, 1] });

    items.forEach(function (item) { observer.observe(item); });

    function startRotate() {
      if (reducedMotion || autorotateMs <= 0) return;
      stopRotate();
      rotateTimer = setInterval(function () {
        if (paused || manuallyPaused || document.hidden) return;
        scrollToItem((activeIndex + 1) % items.length);
      }, autorotateMs);
    }

    function stopRotate() {
      clearInterval(rotateTimer);
      rotateTimer = null;
    }

    function pauseWithResume() {
      paused = true;
      clearTimeout(idleTimer);
      idleTimer = setTimeout(function () { paused = false; }, 8000);
    }

    track.addEventListener('touchstart', pauseWithResume, { passive: true });
    track.addEventListener('scroll',     pauseWithResume, { passive: true });
    section.addEventListener('mouseenter', function () { paused = true; });
    section.addEventListener('mouseleave', function () { paused = false; clearTimeout(idleTimer); });
    section.addEventListener('focusin',    function () { paused = true; });
    section.addEventListener('focusout',   function () { paused = false; });
    document.addEventListener('visibilitychange', function () { paused = document.hidden; });

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        scrollToItem(i);
        pauseWithResume();
      });
    });

    bottomDots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        scrollToItem(i);
        pauseWithResume();
      });
    });

    /* Pause / play toggle button (mobile only) */
    if (pauseBtn && !reducedMotion && autorotateMs > 0) {
      pauseBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        manuallyPaused = !manuallyPaused;
        pauseBtn.classList.toggle('is-paused', manuallyPaused);
        pauseBtn.setAttribute('aria-pressed', manuallyPaused ? 'true' : 'false');
        pauseBtn.setAttribute('aria-label', manuallyPaused ? 'Play slideshow' : 'Pause slideshow');
        /* If resuming, also clear any interaction-based pause */
        if (!manuallyPaused) paused = false;
      });
    } else if (pauseBtn) {
      /* Auto-rotate is off or reduced-motion — hide the button */
      pauseBtn.style.display = 'none';
    }

    setActive(0);
    startRotate();
  }

  /* ── Bootstrap ───────────────────────────────────────────────────── */

  document.querySelectorAll('.jd-section').forEach(function (section) {
    initSwatches(section);
    initQuickAdd(section);
    initCardTracking(section);
    initNavArrows(section);
    if (section.dataset.variant === 'v1') initV1Dots(section);
    if (section.dataset.variant === 'v2') initV2(section);
  });

})();
